import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { listCustomers } from '@vn-propane/shared/client';
import type { Customer, ErrorResponse } from '@vn-propane/shared/client';
import { Plus, Phone, MapPin, Search, User, Loader2 } from 'lucide-react';

export default function CustomersLayout() {
  const { id: orgId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['admin', 'common']);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 500);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery]);

  const fetchCustomers = useCallback(async () => {
    if (!orgId) return;

    if (isFirstLoad.current) {
      setInitialLoading(true);
    } else {
      setSearching(true);
    }

    try {
      const result = await listCustomers({
        path: { org_id: orgId },
        query: debouncedQuery ? { q: debouncedQuery } : undefined,
      });

      if (result.error) {
        const errorResponse = result.error as ErrorResponse;
        if (result.response?.status === 401) {
          navigate('/login');
          return;
        }
        setError(errorResponse.error?.message || t('customers.failedToLoad'));
        return;
      }

      if (result.data) {
        setCustomers(result.data.data);
      }
      setError(null);
    } catch (err) {
      setError(t('customers.failedToLoad'));
      console.error(err);
    } finally {
      setInitialLoading(false);
      setSearching(false);
      isFirstLoad.current = false;
    }
  }, [orgId, navigate, debouncedQuery]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const hasAddress = (customer: Customer) => {
    return !!customer.addressId;
  };

  if (initialLoading) {
    return <div className="flex justify-center p-8">{t('common:labels.loading')}</div>;
  }

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight">{t('customers.title')}</h2>
          <p className="text-muted-foreground">
            {t('customers.customerCount', { count: customers.length })}
          </p>
        </div>
        <Button asChild>
          <Link to={`/organizations/${orgId}/customers/new`}>
            <Plus className="w-4 h-4 mr-2" />
            {t('customers.addCustomer')}
          </Link>
        </Button>
      </div>

      <div className="relative">
        {searching ? (
          <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
        ) : (
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        )}
        <Input
          placeholder={t('customers.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {customers.length === 0 && !debouncedQuery ? (
        <div className="text-center py-12 border rounded-lg">
          <User className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-medium mb-2">{t('customers.noCustomersYet')}</h3>
          <p className="text-muted-foreground mb-4">
            {t('customers.addYourFirstCustomer')}
          </p>
          <Button asChild>
            <Link to={`/organizations/${orgId}/customers/new`}>
              <Plus className="w-4 h-4 mr-2" />
              {t('customers.addCustomer')}
            </Link>
          </Button>
        </div>
      ) : customers.length === 0 ? (
        <div className="text-center py-12 border rounded-lg">
          <Search className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-medium mb-2">{t('customers.noResultsFound')}</h3>
          <p className="text-muted-foreground">
            {t('customers.tryDifferentSearch')}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {customers.map((customer) => (
            <Link
              key={customer.id}
              to={`/organizations/${orgId}/customers/${customer.id}`}
              className="block p-4 border rounded-lg hover:border-primary hover:bg-muted/50 transition-colors"
            >
              <div className="font-medium text-lg mb-2">{customer.fullName}</div>
              <div className="space-y-1 text-sm text-muted-foreground">
                {customer.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-3.5 h-3.5" />
                    <span>{customer.phone}</span>
                  </div>
                )}
                {hasAddress(customer) && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5" />
                    <span className="text-muted-foreground">{t('customers.hasAddress')}</span>
                  </div>
                )}
                {!customer.phone && !hasAddress(customer) && (
                  <div className="text-muted-foreground/50 italic">{t('customers.noContactInfo')}</div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
